import { useState, useEffect, useCallback } from 'react';
import api from '../../api/client';
import { collabApi } from '../../api/collab.js';
import DiscussThread from './DiscussThread.jsx';

/**
 * BOS+ Phase 14 — Messenger app shell.
 *
 * Left: channels (create/join) + a DM starter. Right: the active conversation,
 * rendered by DiscussThread on the selected thread id. Auto-opens the seeded
 * entity demo thread on load so there is always a working thread to see.
 */

const unwrap = (r) => r.data?.data;
const DEMO = { demo: true, title: 'Entity demo thread', kind: 'demo' };

export default function MessengerShell() {
  const [channels, setChannels] = useState([]);
  const [active, setActive] = useState(DEMO);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [dms, setDms] = useState([]);
  const [dmQuery, setDmQuery] = useState('');
  const [dmResults, setDmResults] = useState([]);
  const [error, setError] = useState(null);
  const [unread, setUnread] = useState({});

  const loadChannels = useCallback(async () => {
    try {
      setChannels(unwrap(await api.get('/messenger/channels'))?.items || []);
    } catch (e) { setError(e.response?.data?.message || e.message); }
  }, []);
  const loadDms = useCallback(async () => {
    try {
      setDms(unwrap(await api.get('/messenger/dms'))?.items || []);
    } catch { /* best effort — the DM list is additive to the starter box */ }
  }, []);
  const refreshUnread = useCallback(async () => {
    try {
      const d = unwrap(await api.get('/messenger/unread'));
      const map = {};
      (d?.channels || []).forEach((c) => { map[c.channelId] = c.unread; });
      setUnread(map);
    } catch { /* best effort */ }
  }, []);
  useEffect(() => { loadChannels(); loadDms(); refreshUnread(); }, [loadChannels, loadDms, refreshUnread]);

  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchedQ, setSearchedQ] = useState(''); // the query the current results answer
  useEffect(() => {
    if (searchQ.trim().length < 2) { setSearchResults([]); setSearchedQ(''); return undefined; }
    const t = setTimeout(async () => {
      try { setSearchResults(unwrap(await api.get('/messenger/search', { params: { q: searchQ } }))?.items || []); }
      catch { setSearchResults([]); }
      setSearchedQ(searchQ);
    }, 250);
    return () => clearTimeout(t);
  }, [searchQ]);

  async function openChannelId(channelId) {
    if (!channelId) return;
    try {
      const data = unwrap(await api.get(`/messenger/channels/${channelId}`));
      setActive({ threadId: data.thread.id, title: data.channel.name ? `# ${data.channel.name}` : 'Direct message', kind: data.channel.kind });
      setSearchQ(''); setSearchResults([]);
    } catch (e) { setError(e.response?.data?.message || e.message); }
  }

  async function openChannel(c) {
    try {
      const data = unwrap(await api.get(`/messenger/channels/${c.id}`));
      setActive({ threadId: data.thread.id, title: `# ${c.name}`, kind: 'channel' });
    } catch (e) { setError(e.response?.data?.message || e.message); }
  }

  async function openDm(d) {
    try {
      const threadId = d.thread_id || unwrap(await api.get(`/messenger/channels/${d.id}`))?.thread?.id;
      setActive({ threadId, title: `@ ${d.name}`, kind: 'dm' });
    } catch (e) { setError(e.response?.data?.message || e.message); }
  }

  async function createChannel(e) {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    try {
      const data = unwrap(await api.post('/messenger/channels', { name, visibility: 'public' }));
      setNewName(''); setCreating(false);
      await loadChannels();
      setActive({ threadId: data.thread.id, title: `# ${data.channel.name}`, kind: 'channel' });
    } catch (e) { setError(e.response?.data?.message || e.message); }
  }

  async function joinChannel(c, ev) {
    ev.stopPropagation();
    try { await api.post(`/messenger/channels/${c.id}/join`); await loadChannels(); await openChannel(c); }
    catch (e) { setError(e.response?.data?.message || e.message); }
  }

  useEffect(() => {
    if (!dmQuery) { setDmResults([]); return; }
    const t = setTimeout(() => {
      collabApi.searchUsers(dmQuery).then((d) => setDmResults(d?.items || [])).catch(() => setDmResults([]));
    }, 150);
    return () => clearTimeout(t);
  }, [dmQuery]);

  async function startDm(user) {
    try {
      const data = unwrap(await api.post('/messenger/dms', { memberIds: [user.id] }));
      setDmQuery(''); setDmResults([]);
      setActive({ threadId: data.thread.id, title: `@ ${user.name || user.email}`, kind: 'dm' });
      loadDms();
    } catch (e) { setError(e.response?.data?.message || e.message); }
  }

  return (
    <div data-testid="messenger-shell" className="messenger-app">
      <aside className="messenger-sidebar">
        <div className="messenger-search">
          <input
            data-testid="messenger-search"
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            placeholder="Search messages…"
            aria-label="Search messages"
          />
          {searchResults.length > 0 && (
            <ul className="messenger-search-results" data-testid="messenger-search-results">
              {searchResults.map((r) => (
                <li key={r.id}>
                  <button type="button" className="messenger-search-result" onClick={() => openChannelId(r.channelId)}>
                    <span className="messenger-search-ch">{r.channelName ? `# ${r.channelName}` : 'DM'}</span>
                    <span className="messenger-search-body">{r.body}</span>
                    <span className="muted small">{r.sender_name}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {searchedQ && searchedQ === searchQ && !searchResults.length && (
            <p className="messenger-side-empty" data-testid="messenger-search-empty" role="status">No messages match “{searchQ.trim()}”.</p>
          )}
        </div>
        <div className="messenger-side-head">
          <h3>Channels</h3>
          <button type="button" className="messenger-side-add" data-testid="messenger-new-channel" onClick={() => setCreating((v) => !v)} aria-label="New channel">+</button>
        </div>
        {creating && (
          <form onSubmit={createChannel} className="messenger-newchannel">
            <input data-testid="messenger-new-channel-name" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="channel-name" aria-label="New channel name" />
            <button type="submit" className="btn primary small" data-testid="messenger-create-channel">Create</button>
          </form>
        )}
        <ul className="messenger-channel-list" data-testid="messenger-channel-list">
          {channels.map((c) => (
            <li key={c.id} data-testid="messenger-channel-item" className={active.threadId && active.title === `# ${c.name}` ? 'active' : ''}>
              <button type="button" className="messenger-channel-btn" onClick={() => openChannel(c)}># {c.name}</button>
              {unread[c.id] > 0 && <span className="messenger-unread-badge" data-testid="messenger-unread-badge">{unread[c.id]}</span>}
              {!c.is_member && <button type="button" className="messenger-join-btn" onClick={(e) => joinChannel(c, e)}>join</button>}
            </li>
          ))}
          {!channels.length && <li className="messenger-side-empty">No channels yet</li>}
        </ul>

        <div className="messenger-side-head"><h3>Direct messages</h3></div>
        <div className="messenger-dm-search mention-input">
          <input data-testid="messenger-dm-search" value={dmQuery} onChange={(e) => setDmQuery(e.target.value)} placeholder="Message someone…" aria-label="Start a direct message" />
          {dmResults.length > 0 && (
            <ul className="mention-dropdown" role="listbox">
              {dmResults.map((u) => (
                <li key={u.id} role="option" data-testid="messenger-dm-option" onMouseDown={(e) => { e.preventDefault(); startDm(u); }}>
                  <span className="avatar tiny">{(u.name || u.email || '?').charAt(0).toUpperCase()}</span>
                  <div><div>{u.name}</div><div className="muted small">{u.email}</div></div>
                </li>
              ))}
            </ul>
          )}
        </div>
        <ul className="messenger-channel-list" data-testid="messenger-dm-list">
          {dms.map((d) => (
            <li key={d.id} data-testid="messenger-dm-item" className={active.threadId === d.thread_id ? 'active' : ''}>
              <button type="button" className="messenger-channel-btn" onClick={() => openDm(d)}>@ {d.name}</button>
              {unread[d.id] > 0 && <span className="messenger-unread-badge" data-testid="messenger-unread-badge">{unread[d.id]}</span>}
            </li>
          ))}
        </ul>

        <div className="messenger-side-head"><h3>Demo</h3></div>
        <ul className="messenger-channel-list">
          <li className={active.demo ? 'active' : ''}>
            <button type="button" className="messenger-channel-btn" onClick={() => setActive(DEMO)}>Entity demo thread</button>
          </li>
        </ul>
      </aside>

      <main className="messenger-main">
        <h2 className="messenger-conv-title" data-testid="messenger-conv-title">{active.title}</h2>
        {error && <p className="messenger-error" role="alert">{error}</p>}
        {active.demo ? (
          <DiscussThread subjectType="demo" subjectId="demo" subjectTitle="Messenger demo thread" onRead={refreshUnread} />
        ) : (
          <DiscussThread key={active.threadId} threadId={active.threadId} onRead={refreshUnread} />
        )}
      </main>
    </div>
  );
}
